import React, { useEffect, useMemo, useState } from "react";
import { ReactTFC } from "../../../utils/fp/missing-ts-parts";

import { getLocaleWithInternationalFallback } from "@swo/shared";
import { EventsOverviewPageData, ExtractSwarovskiPageData } from "@swo/storefront";
import { head } from "fp-ts/es6/Array";
import { useLocation } from "react-router";
import { ContentContainer } from "../../../components/_shared-components/arrangement/Container";
import { Spacer } from "../../../components/_shared-components/arrangement/Space";
import { FooterComponent } from "../../../components/FooterComponent/FooterComponent";
import { HeaderComponent } from "../../../components/HeaderComponent/HeaderComponent";
import { PageMetadataComponent } from "../../../components/PageMetadataComponent/PageMetadataComponent";
import { t2 } from "../../../domain/translations/translations";
import { useStore } from "../../../store/hooks/use-store";
import { optToMaybe } from "../../../utils/fp/small-fp-utils";
import { AutoHeaderSpacer } from "../../_layout-helpers/auto-header-spacer";
import { ComponentRenderer } from "../../_layout-helpers/component-renderer";
import { ProductListingPageCategorySelector } from "../../product-listing-page/ProductListingPageCategorySelector/ProductListingPageCategorySelector";
import { EventsOverviewEvents } from "./EventsOverviewEvents";
import { EventsOverviewFilterBar, FilterBarFilter } from "./EventsOverviewFilterBar";
import { getFilterBarFilter, getFilteredEvents } from "./filtering";

export type AppliedFilterT<T> = { filterKey: string; filterValue: T; filterLabel: string };
export type AppliedFiltersT = {
  dateFilter: AppliedFilterT<Date> | undefined;
  countryFilter: AppliedFilterT<string> | undefined;
};

export const EventsOverviewPageLayout: ReactTFC<ExtractSwarovskiPageData<EventsOverviewPageData>> = props => {
  const { locationOrigin } = useStore().state;
  const location = useLocation();

  const tabElements = props.data.eventTypes.map(e => ({
    label: e.label,
    link: e.link,
    isActive: e.type === props.page.eventType,
    expectSimilarLayout: true,
    scrollToTop: false,
  }));

  const localeWithInternationalFallback = getLocaleWithInternationalFallback(props.locale);

  const filters: FilterBarFilter = useMemo(
    () => getFilterBarFilter(props.data.events, props.data.translations, localeWithInternationalFallback),
    [props.data.events, props.data.translations, localeWithInternationalFallback],
  );

  const emptyFilters = {
    dateFilter: undefined,
    countryFilter: undefined,
  };

  const [appliedFilters, setAppliedFilters] = useState<AppliedFiltersT>(emptyFilters);

  useEffect(() => {
    setAppliedFilters(emptyFilters);
  }, [props.page.eventType, location.pathname]);

  const filteredEvents = getFilteredEvents(props.data.events, appliedFilters);

  const facetedFilter = <T,>(filters: AppliedFiltersT) => {
    return getFilteredEvents(props.data.events, filters).length;
  };

  return (
    <React.Fragment>
      <PageMetadataComponent
        {...props.data.metadata}
        locationOrigin={locationOrigin}
        canonicalPath={props.page.path}
        languageIsoCode={props.locale.language.isoCode}
      />

      <HeaderComponent header={props.data.header} version={"normal"} />
      <AutoHeaderSpacer
        componentAfterHeaderTypeName={optToMaybe(
          head(props.data.components.map(c => (typeof c === "string" ? undefined : c.__typename))),
        )}
      />

      <ContentContainer>
        <ProductListingPageCategorySelector
          label={props.data.translations.eventsFilterLabel}
          elements={tabElements.map(t => ({
            className: t.isActive ? "active" : undefined,
            key: t.link,
            name: t.label,
            path: t.link,
          }))}
        />
        <Spacer size={"md"} />

        <EventsOverviewFilterBar
          key={props.page.eventType}
          filters={filters}
          appliedFilters={appliedFilters}
          setAppliedFilters={setAppliedFilters}
          facetedFilter={facetedFilter}
        />

        <EventsOverviewEvents events={filteredEvents} realLocaleCode={localeWithInternationalFallback} />
      </ContentContainer>

      <Spacer size={"lg"} />

      <ComponentRenderer
        components={props.data.components}
        localeForNumberFormat={props.locale}
        translations={t2(props.generalTranslations)}
      />
      <Spacer size={"md"} />
      <FooterComponent {...props.data.footer} />
    </React.Fragment>
  );
};
